import type {ServerError} from '@logistics-frontend/core/utils/network'
import {Button, Modal, Typography, useTransition} from '@yandex-delivery/blocks'
import {useRefresh} from '@yandex-delivery/hooks'
import type {Optional} from '@yandex-delivery/types'
import {applySpec, constant, identity} from '@yandex-delivery/utils'

import {fetchMemoizedPollSharing} from 'src/shared/api'
import {i18n} from 'src/shared/config'
import {DELAY_TIMEOUT_MS} from 'src/shared/consts'
import type {SharingRequest} from 'src/shared/types'

type Props = {
  orderId: string
  onClose: VoidFunction
}

const toSharingKey: (orderId: string) => SharingRequest = applySpec<SharingRequest>({
  order_id: identity,
  order_provider_id: constant('cargo-claims'),
})

export function ExistingOrderModal({orderId, onClose}: Props) {
  const [{payload: pollHandler}, startTransition] = useTransition<Optional<VoidFunction>, ServerError>(undefined, {
    key: 'fetch/sharingKey',
  })

  const acceptHandler = () => {
    startTransition(() => {
      return fetchMemoizedPollSharing(toSharingKey(orderId), startTransition)
    })
  }

  useRefresh(pollHandler, {
    interval: DELAY_TIMEOUT_MS,
  })

  return (
    <Modal mx="s" onClose={onClose} opened>
      <Modal.Header title={i18n('order.existing-order-id.title', 'У Вас есть незавершенный заказ')} />
      <Modal.Body>
        <Typography variant="caption1">
          {i18n(
            'order.existing-order-id.description',
            'Возможно, Вы не завершили предыдущий заказ. Хотите продолжить?',
          )}
        </Typography>
      </Modal.Body>
      <Modal.Footer>
        <Modal.Footer.Controls>
          <Button data-e2e="button-cancel" onPress={onClose}>
            {i18n('order.existing-order-id.control.cancel', 'Отменить')}
          </Button>
          <Button data-e2e="button-save" loading={!!pollHandler} onPress={acceptHandler} variant="action">
            {i18n('order.existing-order-id.control.cancel', 'Продолжить')}
          </Button>
        </Modal.Footer.Controls>
      </Modal.Footer>
    </Modal>
  )
}
